/* eslint-disable @typescript-eslint/no-explicit-any */
import './Navigation.scss';

import { SideMenu } from '@whistler-ui/k2/components/SideMenu';
import { cn } from '@whistler-ui/k2/helpers';
import { useComponentWidth } from '@whistler-ui/k2/hooks';
import { PageRoute } from '@whistler-ui/k2/types';
import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';

import { useBridgeKeeperMutation, useRoleKeeper } from '../../hooks';
import { routes as appRoutes } from '../../routes';
import { useAuthStore, useLayoutStore } from '../../store';
import { useThemeStore } from '../../store/theme';

const componentName = 'c-Navigation';

interface NavigationProps {
  routes: Record<string, PageRoute>;
  routeFilters: {
    filterProductionModeRoutes: (route: [string, PageRoute]) => boolean;
    filterTestModeRoutes: (route: [string, PageRoute]) => boolean;
    customFilters: Array<(route: [string, PageRoute]) => boolean>;
  };
}

export const Navigation = ({ routes, routeFilters }: NavigationProps) => {
  const [can, RoleKeeper] = useRoleKeeper();
  const { mutateAsync: getBridgeKeeper } = useBridgeKeeperMutation();
  const { config } = useThemeStore();
  const location = useLocation();
  const { ref } = useComponentWidth('main-navigation', {
    enableMutationObserver: false,
    transitionProperties: ['max-width', 'min-width', 'width'],
    enableTransitionListener: true,
  });

  const {
    authValues: {
      loggedUser,
      organisationState: { isValidProductionUpgrade },
    },
  } = useAuthStore();

  const [activeRoutes, setActiveRoutes] = React.useState(null);
  const { sidebar } = useLayoutStore();
  const logoUrl = config?.environments?.settings?.menuLogoUrl;

  useEffect(() => {
    document.body.style.setProperty(
      '--menu-offset',
      sidebar.isOpen ? '245px' : '85px',
    );
  }, [sidebar.isOpen]);

  useEffect(() => {
    const validateRoutes = async () => {
      const clonedRoutes: Record<string, PageRoute> = { ...routes };
      try {
        const promises = Object.entries(clonedRoutes).map(
          async ([key, value]) => {
            if (value.feature) {
              const isValid = await getBridgeKeeper(value.feature);

              if (!isValid) {
                delete clonedRoutes[key];
              }
            }
          },
        );

        await Promise.all(promises);
      } catch (error) {
        console.error('validateRoutes', error);
      }
      setActiveRoutes(clonedRoutes);
    };

    validateRoutes();
  }, [routes]);

  const currentRoute: any = useMemo(() => {
    if (!activeRoutes) {
      return null;
    }
    const routeEntry = Object.entries(activeRoutes).find(
      ([, value]: any) => value.url === location.pathname,
    );

    return routeEntry ? routeEntry[1] : ({} as PageRoute);
  }, [location.pathname, activeRoutes]);

  const handleMinimized = (value: boolean) => {
    if (value) {
      sidebar.close();
      return;
    }

    sidebar.open();
  };

  const filteredRoutes = React.useMemo(() => {
    if (!activeRoutes) {
      return [];
    }
    // this is a set of filters that will be applied to the routes, this can be dynamically extended in the routes.tsx file
    // allowing filtering of routes based on the state of the application

    const customFilteredRoutes = routeFilters?.customFilters?.reduce(
      (acc, filter) => acc.filter(filter),
      Object.entries(activeRoutes),
    );

    return customFilteredRoutes.filter(
      isValidProductionUpgrade
        ? routeFilters.filterProductionModeRoutes
        : routeFilters.filterTestModeRoutes,
    );
  }, [activeRoutes, routeFilters.customFilters]);

  if (!loggedUser || currentRoute?.isBlankPage || currentRoute?.isStandAlone) {
    return null;
  }

  return (
    <aside
      className={cn(componentName, 'border-r border-gray-200 z-51')}
      ref={ref}
    >
      <SideMenu
        routes={filteredRoutes as [string, PageRoute][]}
        logoURL={logoUrl || routes?.dashboard.url}
        isMinimized={!sidebar.isOpen}
        handleMinimized={handleMinimized}
        height={config.environments.settings.sideMenuLogoHeight ?? undefined}
        padding={config.environments.settings.sideMenuLogoPadding ?? undefined}
        roleValidations={[can, RoleKeeper]}
        logo={config.assets.logo}
        logoMinimized={config.assets.logoMini}
        context={config.context}
        poweredBy={config.environments.settings.poweredBy}
        showCaretForRoutes={[appRoutes.entitlementsImplement.url]}
      />
    </aside>
  );
};
